/**
 * API client for the Live Performance Lyrics Prompter backend.
 * Wraps all REST endpoints with typed request/response handling.
 */
import type { Song, SetList, UploadResponse, ApiError } from '../types';

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL ?? '/prod';

class ApiClient {
  private baseUrl: string;

  constructor(baseUrl: string) {
    this.baseUrl = baseUrl.replace(/\/+$/, '');
  }

  private async request<T>(path: string, options: RequestInit = {}): Promise<T> {
    const url = `${this.baseUrl}${path}`;
    const headers: Record<string, string> = {
      ...(options.headers as Record<string, string>),
    };

    // Don't set Content-Type for FormData (browser sets it with boundary)
    if (!(options.body instanceof FormData)) {
      headers['Content-Type'] = 'application/json';
    }

    const response = await fetch(url, { ...options, headers });

    if (!response.ok) {
      let errorBody: ApiError;
      try {
        errorBody = await response.json();
      } catch {
        errorBody = { message: response.statusText, statusCode: response.status };
      }
      throw errorBody;
    }

    // Handle 204 No Content
    if (response.status === 204) {
      return undefined as T;
    }

    return response.json();
  }

  // ---- Songs ----

  async uploadSong(file: File): Promise<UploadResponse> {
    const formData = new FormData();
    formData.append('file', file);
    return this.request<UploadResponse>('/songs/upload', {
      method: 'POST',
      body: formData,
    });
  }

  async getSongs(): Promise<Song[]> {
    const data = await this.request<{ songs: Song[] }>('/songs');
    return data.songs;
  }

  async getSong(songId: string): Promise<Song> {
    const data = await this.request<{ song: Song }>(`/songs/${encodeURIComponent(songId)}`);
    return data.song;
  }

  async updateSong(songId: string, updates: Partial<Pick<Song, 'title' | 'artist' | 'tempo' | 'lengthMinutes' | 'lengthSeconds'>>): Promise<Song> {
    const data = await this.request<{ song: Song }>(`/songs/${encodeURIComponent(songId)}`, {
      method: 'PUT',
      body: JSON.stringify(updates),
    });
    return data.song;
  }

  async deleteSong(songId: string): Promise<void> {
    return this.request<void>(`/songs/${encodeURIComponent(songId)}`, {
      method: 'DELETE',
    });
  }

  async reparseSong(songId: string): Promise<void> {
    return this.request<void>(`/songs/${encodeURIComponent(songId)}/reparse`, {
      method: 'POST',
    });
  }

  // ---- Set Lists ----

  async createSetList(name: string): Promise<SetList> {
    const data = await this.request<{ setList: SetList }>('/setlists', {
      method: 'POST',
      body: JSON.stringify({ name }),
    });
    return data.setList;
  }

  async getSetLists(): Promise<SetList[]> {
    const data = await this.request<{ setLists: SetList[] }>('/setlists');
    return data.setLists;
  }

  async getSetList(setListId: string): Promise<SetList> {
    const data = await this.request<{ setList: SetList }>(`/setlists/${encodeURIComponent(setListId)}`);
    return data.setList;
  }

  async updateSetList(setListId: string, updates: Partial<Pick<SetList, 'name'>>): Promise<SetList> {
    const data = await this.request<{ setList: SetList }>(`/setlists/${encodeURIComponent(setListId)}`, {
      method: 'PUT',
      body: JSON.stringify(updates),
    });
    return data.setList;
  }

  async deleteSetList(setListId: string): Promise<void> {
    return this.request<void>(`/setlists/${encodeURIComponent(setListId)}`, {
      method: 'DELETE',
    });
  }

  async addSongToSetList(setListId: string, songId: string): Promise<SetList> {
    const data = await this.request<{ setList: SetList }>(`/setlists/${encodeURIComponent(setListId)}/songs`, {
      method: 'POST',
      body: JSON.stringify({ songId }),
    });
    return data.setList;
  }

  async removeSongFromSetList(setListId: string, songId: string): Promise<void> {
    return this.request<void>(
      `/setlists/${encodeURIComponent(setListId)}/songs/${encodeURIComponent(songId)}`,
      { method: 'DELETE' },
    );
  }

  async reorderSetListSongs(setListId: string, songOrder: string[]): Promise<SetList> {
    const data = await this.request<{ setList: SetList }>(`/setlists/${encodeURIComponent(setListId)}/reorder`, {
      method: 'PUT',
      body: JSON.stringify({ songOrder }),
    });
    return data.setList;
  }
}

export const apiClient = new ApiClient(API_BASE_URL);
